import dr5MoveRzp from "../assets/images/content/dr-5move-rzp.png";
import { GLOSSARY_TERMS } from "./glossary.js";

export const DOMINO = [
  {
    id: "dr",
    title: "Domino Reduction",
    kicker: "Метод топів · крок 1",
    blocks: [
      {
        t: "p",
        c: "**Domino Reduction**, або просто **DR**, — метод, яким збирають як світові топи, так і генератор скрамблів. Ідея збірки DR-ом — не поступова збірка елементів, а поступове «спрощення» куба.",
      },
      {
        t: "p",
        c: "Назва походить від доміно-кубика 3×3×2, який збирається тільки рухами `U D R2 L2 F2 B2`. Саме до такого положення треба звести кубик при збірці DR-ом. Виглядає це так: на двох протилежних сторонах куба є тільки їхні кольори, а ребра середнього шару можна зібрати подвійними рухами.",
      },
      {
        t: "p",
        c: "Збирається доміно так: спочатку робиться **EO**, потім за 0–3 рухи куб приводиться, змінюючи відповідно орієнтацію елементів, до так званого **тригера** — положення куба, коли в стані не-доміно перебуває обмежена кількість елементів, — а потім його вирішують, приводячи до стандартного тригера.",
      },
      {
        t: "list",
        items: [
          "`R U/U' R'` — DR-2E3C",
          "`R U2 R'` — DR-2E4C",
          "`R` — DR-4E4C",
        ],
      },
      {
        t: "p",
        c: "Що означають ці позначення, ви можете зрозуміти, зробивши відповідні рухи і порахувавши елементи різних типів на кубі, які не відповідають описаному вище стану DR. Для отримання хорошого DR виписують якнайбільше EO і перебирають їх, стараючись привести до тригера, а потім до DR.",
      },
      {
        t: "note",
        tone: "tip",
        title: "RZP",
        c: "Етап приведення до тригера називають **RZP**. Варто шукати RZP не довший за 6 рухів, оскільки інакше достатньо важко отримати короткий DR. Тобто якщо ви перевіряєте EO за 4 рухи, варто спробувати на цьому етапі додати 1–2 рухи, щоб змінити кількість орієнтованих елементів до однієї з бажаних — не забуваючи перевіряти обидві осі.",
      },
      {
        t: "p",
        c: "Для того щоб звикнути до сетапів DR-4E4C, я дуже рекомендую тренажер **Tao Yu** з ресурсів нижче: виставте Domino Reduction і 1–2 рухи, потім додайте 3, коли звикнете до 1–2, потім 4 і так далі.",
      },
      {
        t: "p",
        c: "Щодо використання NISS під час пошуку доміно, світчатись варто:",
      },
      {
        t: "list",
        items: [
          "Під час пошуку EO — для знаходження EO в 4 варто перевіряти, чи нема на інверсі EO за 3 рухи за допомогою NISS-трейсингу, якщо на стороні, яку ви перевіряєте, є редукція до 4 поганих ребер за 1 хід.",
          "Після EO варто робити світч, тільки якщо їх декілька найменшої довжини — наприклад, якщо на скрамблі всього 2 трійки, після них варто світчнутись.",
          "Після RZP за 5, або за 6, якщо це JZP (див. [бонусну главу](/bonus#jzp)) чи DR-2E3C.",
        ],
      },
      {
        t: "schedule",
        title: "Розподіл часу при збірці за годину",
        items: [
          { when: "5–10 хв", what: "пошук та виписування EO" },
          { when: "до 30–35 хв", what: "перевірка EO та пошук DR" },
          {
            when: "до кінця",
            what: "вирішення найкращого DR (з часом це займатиме менше часу, тож зможете тримати декілька найкращих DR і працювати з ними)",
          },
        ],
      },
      {
        t: "guideLink",
        title: "Посібник по доміно для космотуристів",
        description:
          "Мій розширений гайд з прикладами саме по пошуку та збірці DR. Ця інформація вже розписана й нижче на сторінці — розділи «Просунутий EO», «RZP — просунуто» та «DR-тригери за типами».",
        url: "https://docs.google.com/document/d/1nlsZpDyeKPBt_5FFn4Ql9wDxENGMnh81/edit?usp=sharing&ouid=106175974727680881899&rtpof=true&sd=true",
      },
    ],
    resourceGroups: [
      {
        title: "Ресурси",
        items: [
          {
            label: "FMC: a guide for finding DRs (зверніть увагу на NISS EO)",
            url: "https://www.speedsolving.com/threads/fmc-a-guide-for-finding-drs-domino-reduction-guide.87402/",
          },
          { label: "Domino Reduction Tutorial", url: "https://youtu.be/Axr0xZSklWE?si=Jvj4Q1adRSNmfcT9" },
          {
            label: "Nissy — інструмент для пошуку EO, DR, HTR та рішення після DR",
            url: "https://nissy.tronto.net/",
          },
          { label: "How to use NISSY for DR FMC analysis", url: "https://youtu.be/ugEQjzAgxqE?si=_Sfww2-VsScVuWgN" },
          { label: "Mallard — ще один інструмент для пошуку рішень", url: "https://joba.me/mallard/" },
          { label: "Тренування тригерів", url: "https://torchlight.github.io/fmc/dr-trainer.html" },
          {
            label: "DR algs with mnemonics — корисні алгоритми (зверніть увагу на комутатори ребер)",
            url: "https://docs.google.com/document/d/1TqVjcA5DW3TzZOtxV1uzvHeYBACA4CP-lECrLuD9WAY/edit",
          },
          { label: "Tao Yu Alg Trainer — тренування збірки DR", url: "https://tao-yu.github.io/Alg-Trainer/" },
        ],
      },
      {
        title: "Статистика",
        items: [
          {
            label: "DR moves + trigger stats",
            url: "https://docs.google.com/spreadsheets/d/17vBxLZN9ePf9GzqMi0iAO9ZQq2eVObWSC7dVMkSMJM8/edit#gid=0",
          },
          {
            label: "DR setup stats (switching last move)",
            url: "https://docs.google.com/spreadsheets/d/1_2PmFv1KyBX335SjSb4oweS0sx804fIhbuP50D-gBj4/edit#gid=1404329911",
          },
          {
            label: "ARM/DRM статистика",
            url: "https://docs.google.com/spreadsheets/d/13fSwUROzP0hZQV1Cn96GndWbPy4UrsHiYTaWFSeWtJs/edit?usp=drivesdk",
          },
          {
            label: "4c4e ARM subtype stats",
            url: "https://docs.google.com/spreadsheets/d/1PAVonITR4HLBMml1-uxJwGRwHIoRWIGdNsK-A_KG_yA/edit#gid=995476732",
          },
        ],
      },
      {
        title: "Просунуті ресурси",
        items: [
          {
            label: "Domino Setup Theory",
            url: "https://docs.google.com/document/d/1Z3O8BY1LbAMF6m55ufXtSdtBVwSJpMArtRrDT-p_CwM/edit#heading=h.qqmnh2cvczc0",
          },
          { label: "Список корисних тригерів крім базових", url: "https://www.cuberoot.me/dr-trigger/" },
          {
            label: "Рекомендації про просунуті тригери від Макніла",
            url: "https://www.speedsolving.com/threads/fmc-advanced-guide-collection-of-tips-for-eo-dr.91676/",
          },
          {
            label: "DR 5move triggers (in progress)",
            url: "https://docs.google.com/spreadsheets/d/18WzVYhWRs7YfhMAx05lgkmMdPJROpGYVL8zmvmOo_qs/edit#gid=2032204350",
          },
          {
            label: "NEW — DR 5 MOVE TRIGGERS (оновлений список)",
            url: "https://docs.google.com/spreadsheets/d/1EXqgkrgq4Vqc3CdVhjpSPsCEK--MmtQ1P9iYqChMlkI/edit#gid=1420174797",
          },
        ],
      },
    ],
  },
  {
    id: "eo-advanced",
    title: "Просунутий EO",
    kicker: "Domino Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Щоб не пропускати короткі EO, варто вивчити [повну таблицю EO](https://docs.google.com/spreadsheets/d/1K38k5BQUV5PQh4ZmeJGNfrRZbnuhVePp4CLqyCIelm4/edit?gid=570063682#gid=570063682) — після кожної збірки звіряйтесь із нею й перевіряйте, чи знайшли ви геть усі 4-рухові EO та всі 5-рухові на осях, де 5 рухів — оптимум.",
      },
      {
        t: "p",
        c: "Загальна стратегія пошуку розширень (за Джеєм): знати всі 4-рухові EO настільки добре, щоб миттєво перевіряти, чи будь-який можливий хід дає 4-руховий випадок для вашого EO. Ось основні прийоми:",
      },
      {
        t: "list",
        items: [
          "**Бічний хід** на грані без поганих ребер — підходить будь-який із 3 можливих ходів: `F R' F` → `L F R' F`.",
          "**Бічний хід**, що приводить до випадку з тим самим (або на 1 коротшим) оптималом: `F R' F` → `U F' L F`.",
          "**Подвійний хід на B**, коли ви сетапите на F і на B немає жодного ребра: `F R' F` → `B2 F R' F`.",
          "**Хід на осі EO** — часто трапляється і в оптимальних рішеннях EO: `U F L F` → `F U F L F`.",
        ],
      },
      {
        t: "p",
        c: "Дуже важливо вміти шукати EO через NISS. **NISS-трейсинг** працює так: якщо на нормалі є неорієнтоване зелено-червоне ребро, на інверсі неорієнтованим буде ребро в позиції червоно-зелений. Тобто одним ходом можна отримати 2 чи 4 погані ребра, протрейсити їх усі й записати EO, якщо рішення на інверсі коротше 5 рухів. Особисто я записую нісс EO не довші за 4 ходи плюс усі їхні 5-рухові розширення — це найефективніше для світчів, хоча записувати одразу 5-рухові теж припустимо, якщо встигаєте їх перевіряти.",
      },
      {
        t: "p",
        c: "**2+2 EO** шукаються так: перевірте, чи після 2 ходів на нормалі виходить 4 погані ребра, з яких 3 мають однаковий колір F/B, а тоді — чи дає 4-те ребро EO за 2 ходи на інверсі. Ще один прийом — трейсити нісс 1 ходу до 8 поганих ребер не за поганими, а за **хорошими** ребрами: для саб4 EO з 8 поганими ребрами форма хороших ребер — це `R U L`, `U R L` або `R L`.",
      },
      {
        t: "note",
        tone: "tip",
        title: "Регулярний трейс",
        c: "Ще одна корисна звичка (хоч я сам роблю її не завжди) — трейсити інверс після кожного першого ходу, що змінює орієнтацію ребер до 2–8 поганих. Тобто, перевіряючи EO на F/B, ви одразу трейсите і після F, і після B.",
      },
      {
        t: "p",
        c: "**2 погані ребра.** Знаходяться і розширюються найпростіше: для `F R' F` майже будь-який перший хід, окрім `B`, `B'`, `F2`, дає саб5 EO. Не забувайте перевіряти і `F R2 F`, і `B U2 B` — а якщо обидва погані ребра на F, ще й тип `F R F`.",
      },
      {
        t: "p",
        c: "**4 погані ребра.** Тут вже потрібно розуміти кілька принципів, за якими генеруються розширення:",
      },
      {
        t: "list",
        items: [
          "Порядок ходів не має значення: `R U' F` = `U' R F`.",
          "Різні бічні перші ходи дають різні оптимальні випадки: `L U' R' F` → `B2 L R2 F`; `U L F` → `F2 R' F`.",
          "Хід `F` створює 3-руховий випадок: `D R U' F` → `F U' L' F` або `F2 D L F`.",
        ],
      },
      {
        t: "note",
        tone: "info",
        title: "Довідка",
        c: "5-рухових розширень 4-рухових EO на осях з 4 поганими ребрами зазвичай понад 30 — записувати всі варто лише за дефіциту EO. І не забувайте про нісс-трейсинг: 1 хід до інших 4 поганих або до 2 поганих ребер. Курйозний 5-ходовик про запас: `F B' U F B`.",
      },
      {
        t: "p",
        c: "**6 поганих ребер.** Один з найскладніших випадків для повного покриття розширень: доведеться добре знати всі 4-ходівки, а подекуди й перебирати перші ходи грубою силою. Кожна 4-ходівка — це або 1 хід до 4 поганих, або 1 хід до 2 поганих, або 1 хід до `F U2 B` (перевіряйте і `F U2 B`, і `B L2 F`). Рідкісний, але важливий 4-ходовий випадок — `F U F B`; саме тому завжди перевіряйте, чи дає 1 хід до 8 поганих 4-ходове EO при розподілі ребер 2/3/1 чи 3/2/1 по F/S/B.",
      },
      {
        t: "p",
        c: "Якщо розподіл ребер 4 на F і 2 на B, оптимум — 4 ходи через один з 2 подвійних ходів до `F U2 B`; це можна використати для генерації розширень, наприклад `U F U2 B` → `U' L2 F' L2 B`. Для нісс EO на осях з 6 поганими перевіряйте щонайменше 1 хід до 2, 1 хід до 4 і 1 хід до 8 поганих.",
      },
      {
        t: "p",
        c: "**8 поганих ребер.** Трохи простіше за 6 поганих у генерації розширень — ті самі принципи. Варіацій зазвичай більше, бо для EO виду `<ходи> F <ходи> B` завжди можна спробувати й `<ходи> F' <ходи> B` — це подвоює кількість знайдених EO. Додаткові прийоми: сетап до `F B` (`R B U' F` → `U R F B`); 1 хід до інших 8 поганих (`R B L U F` → `F' R B U' F`); подвійний хід, що покращує випадок після F (`B U' R' F` → `U2 B R' F`). У ніссі найчастіше корисний 1 хід до 4 поганих, але й 1 хід до інших 8 поганих варто перевіряти.",
      },
      {
        t: "p",
        c: "**10–12 поганих ребер.** Дуже погані випадки без лінійних рішень саб6 — перевіряйте 1 хід до 8 поганих, 1 хід до 6 поганих і 2 ходи до 2/4 поганих (трейсячи їх), а потім переходьте на інші осі.",
      },
    ],
  },
  {
    id: "rzp-advanced",
    title: "RZP — просунуто",
    kicker: "Domino Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Коли ви почнете перевіряти більше EO і більше різних RZP, стандарт хорошого DR можна знижувати — спершу до 12 рухів, потім до 11. З досвідом також не варто витрачати багато часу на RZP, які інтуїтивно виглядають погано (окрім хіба що дуже коротких, за 3 ходи чи менше) — рухайтесь далі, у гіршому разі ви просто побачите нове рішення пізніше.",
      },
      {
        t: "note",
        tone: "tip",
        title: "Премув",
        c: "У пошуку RZP допомагає ідея премува: якщо у вас є EO виду `U (R F U)`, перевіривши цей EO та варіації на інверсі, спробуйте EO на іншій осі — і подивіться, чи дає той самий `U` вже RZP на другій стороні скрамблу (або навіть `U (U)` RZP при короткому EO). Іноді премув можна робити навіть не першим ходом нісс EO, якщо всі EO вже перевірені, а хорошого DR все ще немає.",
      },
      {
        t: "p",
        c: "**JZP** — сетап DR без чверть-обертів на гранях, де ви вирішуєте DR (наприклад, для 4e4c підійде `B2 R2 U2 R`, але не `R2 U' R`; детальніше про вимоги — в [бонусній главі](/bonus#jzp)). У більшості скрамблів варто шукати JZP за 6 рухів, а також RZP до DR-7C8E і DR-8C8E, щойно ви їх опануєте — обидва сабсети розібрано в наступному розділі.",
      },
      {
        t: "p",
        c: "Наявність JZP на одній осі DR означає, що після світчу JZP буде і на іншій осі, тому рішення RZP з JZP у середньому суттєво коротші. З JZP також вигідно ставати такі RZP, як DR-4C6E, DR-2C2E, DR-2C4E: самі по собі вони не дуже хороші, але з JZP до них легко застосувати HTR-трюки, і DR виходить за 5–6 рухів.",
      },
      {
        t: "p",
        c: "**ARM** (Axial Reduction Minus) показує, наскільки RZP близький до JZP на кожній з осей, і дозволяє розумніше вирішувати, чи варто світчитись. Наприклад, `R U` дає AR-2C1E на осі U/D і AR-0C0E на осі R/L — тому що на U/D є 1 ребро на шарі M і 2 «розвернуті вбік» кути, а на R/L таких елементів немає. Щоб порахувати ARM на іншій осі, порахуйте неорієнтовані кути без кольору F/B на білій/жовтій гранях і неорієнтовані ребра на шарі E. Таблиці ймовірностей саб7 DR по підтипах ARM — у [бонусній главі](/bonus#arm).",
      },
    ],
  },
  {
    id: "dr-triggers",
    title: "DR-тригери за типами",
    kicker: "Domino Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Коли RZP приведений до одного з тригерів, подальші поради відрізняються залежно від типу. Ось практичні нотатки по кожному з них — і по кількох рідкісних, але корисних тригерах.",
      },
      {
        t: "p",
        c: "**4C4E.** Найпопулярніший тригер — тренуйте його на [Tao Yu Alg Trainer](https://tao-yu.github.io/Alg-Trainer/), вмикаючи DR за 6 і менше рухів. Не забувайте і про `R U2 F2 R`. Ось список усіх рішень, що дають додатковий RZP для DR за 5 рухів, разом з підтипом ARM (від Карнауха):",
      },
      {
        t: "image",
        src: dr5MoveRzp,
        alt: "Таблиця 5-рухових рішень, що дають додатковий RZP для DR, з позначенням підтипу ARM",
        caption: "Список рішень, що дають додатковий RZP для DR за 5 рухів, із підтипом ARM (Карнаух).",
      },
      {
        t: "p",
        c: "**3C2E.** Здебільшого тривіальний, але кілька складних варіацій варто знати: `F2 L2 R U R`; ланцюжок еквівалентних `B2 U2 R U R` = `F2 D2 L D L` = `R2 B2 L U L` = `R2 F2 R D R`; `L' U L' U' L`. Розширення `R U' R'` → `B2 R U' R'` → `F2 L D' L` змінює сабсет DR і часто корисне як додатковий хід під час сетапу тригера. При AR-0C0E з непарними кутами спробуйте за 1–2 ходи створити DR-3C2E — такі RZP за 7 рухів іноді дають DR за 11.",
      },
      {
        t: "p",
        c: "**4C2E.** Теж досить тривіальний — або короткий сетап до тригера, або одразу відкидайте RZP. Стежте за зсувом RZP, коли є колонка неорієнтованих елементів, наприклад `L U2 L2 D' L`.",
      },
      {
        t: "p",
        c: "**4C6E.** Майже завжди добрий лише як JZP, але іноді можна засетапити `R U2 L`, коли є 2 пари і лінія, наприклад `F2 D R U2 L`.",
      },
      {
        t: "p",
        c: "**7C8E.** Веселий тригер із двома варіаціями — `R U L` і `R' U L`. Обом потрібна лінія орієнтованих елементів знизу, квадрат зверху й хоча б одна лінія неорієнтованих знизу; другий варіант простіше засетапити — 2 лінії неорієнтованих знизу і квадрат зверху гарантують тригер або доворіт від нього.",
      },
      {
        t: "list",
        items: [
          "Сетап `R U' L F2`: 2 сусідні ребра з одного боку, Г-подібна форма з іншого — потрібна хоча б 1 пара неорієнтованих елементів, прилегла до Г-форми.",
          "Сетап `R U' L R2`: ребро з одного боку, квадрат + ребро з іншого — пробуйте приєднати пару до ребра (`R2`) або ребро до пари (`U2 F2`), якщо перший спосіб не спрацював.",
        ],
      },
      {
        t: "p",
        c: "Обов'язково перевірте статистику ARM для 7C8E — тут багато випадків із 0% шансом на саб7 DR і багато з шансом вище 30%. Для 7C8E з 1 ребром AR часто варто шукати RZP у 4C4E. Варіації тут трапляються часто: `U R' U L` → `U' L' U R` (інший сабсет), `L' U R` → `D2 L' U R` (інший сабсет), `U2 L U R` → `D2 R U L` (інша кількість ребер).",
      },
      {
        t: "p",
        c: "**8C8E.** Вимагає знання HTR і JZP. Спершу порахуйте кути поза JZP. Якщо їх 4 — вирішуйте, як у HTR, і завершуйте `R L`.",
      },
      {
        t: "list",
        items: [
          "Сетап `R L U B2 R2 B2`: 4 кути форми 4a і 4 ребра поза тригером `R L` — засетапте останнє ребро (`F2 R2 F2` / `F2 L2 B2` / `B2 R2 B2` / `B2 L2 F2`), наприклад `B2 L2 F2 D R L`.",
          "Сетап `R L U F2 U B2`: 4 кути форми 4b, 2 ребра поза тригером — сетапте `U R2 U`, наприклад `B2 D' L2 D R L`.",
          "Сетап `R L U F2 D B2`: те саме, але з 6 ребрами — легше сетапити, дивлячись на елементи, що вже підходять під `R L`, а не на погані, і потім інвертувавши останній чверть-оберт.",
        ],
      },
      {
        t: "p",
        c: "Зі статистикою ARM для 8C8E стало відомо більше:",
      },
      {
        t: "list",
        items: [
          "DR-8C8E AR-4C3E добре підходить не лише для рішень типу `U R2 D R L`, а й з додатковим RZP у 7C8E, що дає рішення тієї ж довжини, наприклад `L D R' U' L'`.",
          "AR-5C3E і AR-6C3E теж хороші з RZP у 7C8E, причому AR-6C3E має другий найкращий шанс на саб7 DR.",
          "AR-2C1E має непогані шанси на саб7, але лише ходом `U`, що перетворює його на AR-6C3E.",
          "AR-3C1E завжди 6 або 7 ходів — через `R L` до 4-рухового 3C2E, або подвійний хід до цього.",
        ],
      },
      {
        t: "p",
        c: "**Інші тригери.** Рідкісні, але корисні тригери — DR-2E, DR-4E, DR-3C6E, що відповідають `R U2 F2 U2 R`, `R U2 D2 L` і `R' U D2 L`; 3C6E легко впізнати по 2 парах і псевдопарі. Для решти дивіться [оновлений список 5-рухових тригерів](https://docs.google.com/spreadsheets/d/1EXqgkrgq4Vqc3CdVhjpSPsCEK--MmtQ1P9iYqChMlkI/edit?gid=264595521#gid=264595521) — вивчити їх усі для щоденного використання практично нереально, але сам процес дає інтуїцію, який RZP «виглядає добре» і вартий довшого пошуку.",
      },
    ],
  },
  {
    id: "htr",
    title: "Half Turn Reduction",
    kicker: "Метод топів · крок 2",
    blocks: [
      {
        t: "p",
        c: "Наступним спрощенням куба після отримання DR буде **HTR** — Half Turn Reduction. Як випливає з назви, це положення куба, коли його можна зібрати тільки подвійними рухами. Якщо куб перебуває у стані HTR, всі наклейки збігаються або протилежні кольору центру грані, на якій вони є — але це не гарантує наявність HTR.",
      },
      {
        t: "p",
        c: "Наприклад, під цей опис підпадає N-perm і H-perm, проте другий можна вирішити тільки подвійними рухами (як саме — задача для читача), а перший ні. Як виглядає справжній HTR, ви можете зрозуміти, покрутивши кубик тільки подвійними рухами.",
      },
      {
        t: "guideLink",
        title: "Путівник по ХТР для космотуристів",
        description:
          "Мій гайд зі складання HTR. Ця інформація вже розписана й нижче на сторінці — розділи «Як розпізнати HTR», «Як вирішити HTR» та «Просунутий HTR».",
        url: "https://docs.google.com/document/d/1juG8Y5J1aX3X1En8Ewe_JEXEha0-X5Ox/edit?usp=sharing&ouid=106175974727680881899&rtpof=true&sd=true",
      },
    ],
    resourceGroups: [
      {
        title: "Ресурси",
        items: [
          {
            label: "FMC: a guide for finding HTRs, once you've found a DR (Макніл)",
            url: "https://www.speedsolving.com/threads/fmc-a-guide-for-finding-htrs-once-youve-found-a-dr.87220/",
          },
          { label: "HTR Tutorial for FMC — оглядовий туторіал", url: "https://youtu.be/ZGqC0rzm628" },
          {
            label: "Corners in DR.pdf — найпростіший метод кутів, таблиця положень (вчити тільки базові)",
            url: "https://drive.google.com/file/d/1lYQnaF1VuiR7EDfi1DUcIJM0FYC2B3mo/view?pli=1",
          },
          {
            label: "HTR subset recognition tutorial — просунутий метод кутів, сабсети HTR",
            url: "https://www.youtube.com/watch?v=ijBT4xHOF1w",
          },
          {
            label: "HTR better smh (v2) — доповнення, таблиця від Вена",
            url: "https://docs.google.com/spreadsheets/d/1ZhulzHeNgv2Hiy4x9R1MzsMRlPlI-E8DRiHFJlPlboM/edit#gid=0",
          },
          { label: "Діаграма HTR", url: "https://333.fm/tutorial/htr-diagram" },
          {
            label: "HTR Corner Case Funnel — візуалізація випадків",
            url: "https://docs.google.com/drawings/d/13YC_TQah9RZXC75vAhya0lruagiLDpXExuMgqCFCNTM/edit",
          },
          { label: "Тренажер HTR", url: "https://speedcubedb.com/htr/" },
          { label: "DR Bounding Tutorial", url: "https://www.youtube.com/watch?v=eqTVMT35u5M" },
        ],
      },
      {
        title: "Приклади збірок",
        items: [
          {
            label: "100 Example Solves від Jack Love (без текстових пояснень, PDF)",
            url: "https://drive.google.com/file/d/1uJrk7hT3krJiYubOytAnnP8I1AQe0S6N/view",
          },
          {
            label: "Road to WR 1/7 — First official mean with DR+HTR (пояснення збірок Макніла)",
            url: "https://youtu.be/hGqoWSAT0ww?si=NfRyKtcary01I_Ar",
          },
          { label: "20 fmc single — повне відео збірки Вена", url: "https://youtu.be/HqqHI_ePL88?si=xc8wZ0Hd-pto-9vs" },
          { label: "Radek FMC — YouTube канал з відео збірок", url: "https://www.youtube.com/channel/UC3AK9ETaWsUOrp5BnQRvI9Q" },
          {
            label: "FMC attempt with comments — пояснення збірок Кіпріана",
            url: "https://docs.google.com/document/d/1_6hfBXK3cK-xZ1f7VjRH8EtSZ5hpLuV7TNFGT1zkZw8/edit?usp=drivesdk",
          },
          {
            label: "FMC League 2023 — купа збірок топів на одних скрамблах",
            url: "https://docs.google.com/spreadsheets/d/1jLaAKmvs1R9DG9zftTUVnr-6f6eLl1giMiG_4Cf0o_A/edit#gid=0",
          },
          {
            label: "[TIER 2] FMC League — Season 2 (2024)",
            url: "https://docs.google.com/spreadsheets/d/1ygaMZA4OZJ4rOVOM7fzwcK1WoHHSxftesUUCqB68eyc/edit#gid=0",
          },
          {
            label: "[TIER 1] FMC League — Season 2 (2024)",
            url: "https://docs.google.com/spreadsheets/d/1pSFPqupQuLYn0z8LNhZiw0hwz9nYGIhXXRHk2-p_9H4/edit#gid=1028538498",
          },
          {
            label: "Sub 22 Official Mean Stats",
            url: "https://docs.google.com/spreadsheets/d/17L6vArP8t_5yPIeCpiQpp66YBZcLcbxS10hzYtnB-XE/edit?gid=0#gid=0",
          },
        ],
      },
      {
        title: "Статистика",
        items: [
          {
            label: "HTR Subset Stats — по всіх сабсетах з урахуванням ребер",
            url: "https://docs.google.com/spreadsheets/d/17iChOoeB205t3wHTXMLrjM9si2vtxRgFeYR2z3pn1i0/edit#gid=164028783",
          },
          {
            label: "DR Finish Move Counts — статистика по кількості QT",
            url: "https://docs.google.com/spreadsheets/d/1tv8rwo2GF9IWuywFTnde_YlikIPBQvHqW9pysNG6BPM/edit#gid=0",
          },
        ],
      },
      {
        title: "Просунуті ресурси",
        items: [
          {
            label: "Using Hyper-Parity To Solve Corners In Domino Reduction (документ в описі)",
            url: "https://youtu.be/EvrD_bfjTvI?si=vRGm38AEcTi1a4Mp",
          },
          {
            label: "Corner Swap Insertions — корнер-свопи для вирішення ребер (документ в описі)",
            url: "https://youtu.be/zHRx5bzup3c?si=K90wKPO5YEmFNiou",
          },
          {
            label: "HTR Theory — просунута теорія HTR від автора гіперпарітету (документ в описі)",
            url: "https://youtu.be/9NIGHrdUyEE?si=VgS5ACrIulIGu9ki",
          },
          {
            label: "DR Flipping",
            url: "https://docs.google.com/document/d/1hZiNqO2vLKap265YQfjgnKlK2RzR18LtnCFrGSf8qAA/edit#heading=h.6k5sfeko02qv",
          },
        ],
      },
    ],
  },
  {
    id: "htr-recognize",
    title: "Як розпізнати HTR",
    kicker: "Half Turn Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Кількість qt (quarter turns) до вирішення завжди від 0 до 5 і залежить від кількості та форми неправильно зорієнтованих кутів. Формою кутів визначається 14 сабсетів HTR (12 унікальних — 2 пари є інверсіями одна одної): якщо неправильно зорієнтовано 0 або 8 кутів — це **0c** (8c завжди в слайс-русі від 0c); 2 або 6 — **2c** (6c у слайс-русі від 2c); 4 кути мають 2 можливі форми — **4a** (як `R`, усі 4 можна поставити на 1 сторону подвійними рухами) і **4b** (як `R U2 R`, завжди є рівно 2 осі з розподілом кутів 3 до 1). Кількість qt дописується після форми — наприклад, 2c3 означає 2 (або 6) неправильно зорієнтованих кути й HTR за 3 qt.",
      },
      {
        t: "p",
        c: "Сетапи для всіх можливих випадків HTR:",
      },
      {
        t: "commutatorTable",
        rows: [
          { alg: "R", result: "4a1" },
          { alg: "R U2 R2 F2 R", result: "4a2" },
          { alg: "R U2 R' U2 R2 F2 R", result: "4a3" },
          { alg: "R U2 R' U2 R2 F2 R F2 R", result: "4a4" },
          { alg: "R U2 R", result: "4b2" },
          { alg: "R U2 R2 F2 R F2 R", result: "4b3" },
          { alg: "R U2 R' U2 R2 F2 R D2 R", result: "4b4" },
          { alg: "L' B2 R' U2 R U2 L F2 L2 U2 R", result: "4b5" },
          { alg: "L' B2 R' U2 R'", result: "2c3" },
          { alg: "L' B2 R' U2 R U2 L", result: "2c4" },
          { alg: "L' B2 R' U2 R U2 L' U2 L U2 R2", result: "2c5" },
          { alg: "R L U2 R L", result: "0c0" },
          { alg: "R U2 R2 F2 R F2 R2 U2 R", result: "0c3" },
          { alg: "R U2 R' F2 R2 U2 L' U2 L", result: "0c4" },
        ],
      },
      {
        t: "p",
        c: "**0c-випадки.** Дуже рідкісні, тому мало практикуються, але важливо відрізняти справжній HTR (0c0) від фальшивого. Найпоширеніша ознака фальшивого HTR — 3 кути одного кольору на одній стороні. Є лише 1 фальшивий випадок без жодної такої сторони: усюди розподіл кутів 2-2, але однакові кольорові смужки на протилежних гранях «перпендикулярні» одна одній — з'єднання через `R2`/`L2` дає лише 1 спільний кут замість смужки.",
      },
      {
        t: "p",
        c: "Якщо HTR фальшивий, далі перевіряють **блайнд паріті** — парність кількості кутів у циклі (вона завжди збігається з парністю кількості qt після DR: якщо ви не збираєте наосліп, порахуйте кількість qt у скрамблі й у всьому, що зроблено до DR, — вийде та сама парність). Парна кількість кутів у циклі — 0c4, непарна — 0c3.",
      },
      {
        t: "p",
        c: "**4c-випадки.** Спочатку визначте форму (4a чи 4b), потім зробіть блайнд трейс, щоб отримати парність — для 4a залишаються 1/3 або 2/4 qt, для 4b — 2/4 або 3/5. Далі перевірте форму на інверсному скрамблі: подивіться на 2 осі орієнтованих кутів (наприклад U/D і F/B) — якщо на жодній немає 3 кутів одного кольору, на інверсі буде 4a, інакше 4b. 4b3/4a3 і 4b4/4a4 — пари інверсій одна одної, тож коли форма на нормалі й інверсі відрізняється, парність дає остаточну відповідь.",
      },
      {
        t: "list",
        items: [
          "`R' F2 U2 F2` (нормаль) → 4b, непарна парність, форма на інверсі теж 4b → **4b5** (4b2/4b4 відсіяні парністю, 4b3 — тим, що форма однакова з обох боків).",
          "`R U2 R2 F2 R` → 4a, парна парність, на інверсі теж 4a → **4a2**.",
          "`R U2 R2 F2 R F2 R` → 4b, непарна парність, на інверсі 4a → **4b3**.",
        ],
      },
      {
        t: "note",
        tone: "tip",
        title: "Практика",
        c: "Спробуйте визначати сабсет самостійно на інших сетапах 4a і 4b з таблиці вище.",
      },
      {
        t: "p",
        c: "**2c-випадки.** Спочатку перевірте парність — вона одразу відсікає половину варіантів (2c може мати 3, 4 або 5 qt). При парній парності відповідь однозначна — 2c4. При непарній лишаються 2c3 і 2c5: подумки поміняйте місцями 2 неправильно зорієнтовані кути (і зорієнтуйте їх) — якщо результат буде фальшивим HTR, це 2c3, інакше 2c5. Якщо неправильних кутів 6, зробіть `M` (для R/L DR), щоб звести до 2, розпізнайте випадок і скасуйте хід.",
      },
    ],
  },
  {
    id: "htr-solve",
    title: "Як вирішити HTR",
    kicker: "Half Turn Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Загальна стратегія — зменшувати кількість qt до **4a1** із 4 поганими ребрами (сетап тригера `R`) або до **4b2** із 2 поганими ребрами (сетап `R U2 R`). Після виконання qt варто світчатись на інверсний скрембл, а от світч після подвійного руху при вирішенні HTR сенсу не має.",
      },
      {
        t: "p",
        c: "У 95%+ збірок знадобляться лише ці випадки, від найлегшого до найскладнішого: 0c0, 4a1, 4b2, 2c3, 4a2, 4b3/4a3, 2c4 — решта в середньому мають дуже довгі фініші, тож уникайте їх, поки не відчуєте впевненість. Аналізуйте свої DR за допомогою [Nissy](https://nissy.tronto.net/) ([як користуватись](https://youtu.be/ugEQjzAgxqE?si=_Sfww2-VsScVuWgN)) — дивіться на оптимальні та оптимальні+1 фініші й запам'ятовуйте шляхи, якими вони отримані.",
      },
      {
        t: "p",
        c: "**0c0.** Рідкісний, але дуже хороший випадок. Ребра вирішуються тригерами `R U2 F2 U2 R` (варіації `R D2 F2 D2 R`, `R U2 B2 D2 L`, `R D2 B2 U2 L`) для 2 чи 4 поганих ребер, і `R U2 D2 R`. Якщо поганих ребер 6 чи 8 — зведіть до 4a1 із 4 поганими ребрами одним qt і вирішуйте звідти.",
      },
      {
        t: "p",
        c: "**4a1.** Рідкісний, але по суті кожен HTR у якийсь момент проходить через 4a1. З 4 поганими ребрами просто поставте всі кути й ребра на одну сторону та зробіть тригер `R`. Часто вдається поставити все, крім 1 ребра, — тоді рішення скорочується в 2e-тригер, наприклад `R' F2 U2 F2 R`. Якщо поганих ребер не 4 — або вирішіть кути в HTR із 2/4 поганими ребрами і довирішіть звідти, або зробіть регресію qt в 4b2 із 2 поганими ребрами (сетап `R U2 R`).",
      },
      {
        t: "p",
        c: "**4b2.** Дуже частий і найважливіший для опанування випадок. Приклад: `R U2 R' U2 L2 F2` — 4b2 з 2 поганими ребрами, вже засетапленими на тригер `L' U2 L`, але прямий тригер дасть фальшивий HTR — це і є **парітет 4b2**. Виправляється (чи створюється) переміщенням 3 поганих кутів на 1 шар DR і виконанням подвійного руху DR по ньому — наприклад `F2` (чи `U2`), потім `L2` (чи `R2`), а тоді сетап тригера `R U2 R`.",
      },
      {
        t: "p",
        c: "Одразу після зведення до 4b2 перевірте парітет: приведіть кути до форми тригера 1 ходом, виконайте тригер, перевірте — фальшивий HTR чи ні, і скасуйте перевірку. З парітетом потрібна непарна кількість подвійних DR-рухів із 3 кутами на шарі DR (майже завжди 1), без парітету — парна (майже завжди 0). Якщо на шарі DR лишається 2 кути, подвійний рух DR не змінює парітет — це дає рішення на кшталт `R2 U2 D2 L' U2 L`. З 4 чи 0 поганими ребрами — виправте парітет і зробіть 1 qt до 4a1 із 4 поганими ребрами. З 6 поганими ребрами — те саме, що з 2 (перевірте через `M`, якщо DR по R/L), сетап тригера `R U2 L` замість `R U2 R`.",
      },
      {
        t: "p",
        c: "**2c3.** Теж дуже частий і другий за важливістю. Розділіть кути так, щоб на кожному шарі DR був рівно 1, і зробіть qt до 4b2 з 2 поганими ребрами — вирішуйте звідти. Перевага: переходячи з 2c3, парітету 4b2 можна не боятись. Наприклад, `L' B2 R' U2 R' U2 F2 R2 B2` → розділити кути (`F2`, бо `B2` дало б 4b2 з 4 поганими) → `L` до 4b2 2e → якщо є парітет, він виправляється тим самим qt, що робив редукцію, у зворотному напрямку (тут `L2`): `F2 L' D2 B2 R' U2 R`.",
      },
      {
        t: "p",
        c: "**4a2.** Відносно рідкісний, хороший лише з 4 поганими ребрами. Приклад: `R U2 R2 F2 R` — приведіть кути до діагональної форми і виконайте qt до 4a1 з 4e.",
      },
      {
        t: "p",
        c: "**4b3/4a3.** Неприємний, але простий випадок для крайнього використання. 4b3 (`R U2 R2 F2 R F2 R U2 R2 F2 D2 B2 L2`) вирішується сетапом кутів як для `R U2 R`, а потім `R' F2/B2 R` до 4a1 — обирайте той з `B2 L U2 L'` / `B2 L D2 L'`, що не дає 4a1 з 2/6 поганими ребрами (`F2 R D2 R'`/`F2 R U2 R'` тут не підходять). 4a3 (`R U2 R' U2 R2 F2 R U2 F2 L2 F2 D2 U2`) зводиться qt-ходом до 4b2 з гарантованим парітетом — але слідкуйте, щоб не отримати замість цього 4b4: перевірте форму на інверсі одразу після qt, і за потреби зробіть `U2`/`D2` перед qt, обираючи той, що не дає 4a1 з 4 поганими ребрами замість цього.",
      },
      {
        t: "p",
        c: "**2c4.** Найпоширеніший випадок HTR — і найнеприємніший, бо однаково часто буває і 11-, і 14-оптимальним. Покладіть обидва погані кути на 1 шар DR (з 6 поганих — покладіть 2 хороших на 1 сторону і зробіть qt на стороні з усіма поганими) і виконайте qt до 2c3 — але перевірте, чи не вийшло замість цього 2c5: якщо так, додатковий півоберт, що не чіпає 2 кути на тому самому шарі DR, виправляє це (можна також підібрати іншу вісь для qt-зведення).",
      },
    ],
  },
  {
    id: "htr-advanced",
    title: "Просунутий HTR",
    kicker: "Half Turn Reduction · поглиблено",
    blocks: [
      {
        t: "p",
        c: "Для глибшого занурення варто вивчити [статистику сабсетів HTR](https://docs.google.com/spreadsheets/d/17iChOoeB205t3wHTXMLrjM9si2vtxRgFeYR2z3pn1i0/edit#gid=164028783) та [теорію HTR](https://docs.google.com/document/d/1HjS92UBCeCasbW44ezE0fjt5vmWQvlfkaC4rA113nmw/edit), окрім аналізу чужих рішень і Nissy. Загальна рекомендація — перед світчем чи ніссом пробувати якнайбільше шляхів HTR з одного скрамблінгу, це економить час скрамблінгу.",
      },
      {
        t: "p",
        c: "**4a1.** Пробуйте якнайбільше варіацій і розширень — якщо рішення закінчується на `L2 D2 R`, спробуйте `U2/D2 L2 D2 R`. За наявності часу пробуйте розширення до 3q — вони часто дають оптимал навіть для 4a1 4e (найчастіше через 4b2 2e, іноді 4b2 4e чи вирішення кутів HTR з подальшими ребрами). Тригер `U D F2 U' D' R` (+ варіації) інколи корисний, особливо коли скорочується з DR. **Світч:** за 1–2 ходи після регресії до 4b2 2e; за 1–2 ходи після редукції до 0c0 2e/4e (з трейсом ребер); за 1 хід після регресії до 4b2 4e.",
      },
      {
        t: "p",
        c: "**4b2 2e.** Тригер `R U2 F2 B2 L` — чітка форма без ребер, прикріплених до кутів. При короткому HTR з `R U2 R'` або дефіциті HTR спробуйте розширення `R S2 U2 R` і варіацію `R' D2 R2 U2 R` (хоча їх часто знаходять і як вставку 2e2e). `R S2 U2 R` також працює при хорошому FR-випадку з неправильним парітетом — вставка `R' U2 S2 U2 R` (H-перм без останніх 4 ходів) змінює парітет флопі. Якщо до редукції в 4a1 4e лишається 1–2 ходи, спробуйте нісс-трейсинг замість світчу всліпу — це економить час. За наявності часу пробуйте 4q-рішення: регресуйте до 2c3 2e/4e і сетапте їхні 5–6-рухові тригери — це часто дає HTR за 7–9 рухів, іноді оптимальні. **Світч:** за 1–2 ходи після регресії до 2c3 2e/4e; за 1–3 ходи після редукції до 4a1, якщо нісс-трейс хороший.",
      },
      {
        t: "p",
        c: "**4b2 4e.** Тригер `R U2 F2 R` (важко сетапити довше за 1–2 ходи) — див. [документ Джека Лі по 4b2+4e](https://docs.google.com/spreadsheets/d/1X7FVIlmsA23NakbH-xFO0xZUlgGXrm_PnBo1Y3Yi5Xk/edit?usp=sharing). Варіація `R D2 B2 L` дає інший парітет флопі-редукції. Ті самі поради, що й для 4b2 2e (варіації, нісс-трейс), але цей сабсет варто завжди розглядати як найкращий 4q-кейс — він оптимальний з 4q більш ніж половину часу, тож завжди пробуйте регресію до 2c3 2e/4e. **Світч:** ті самі рекомендації, що й для 4b2 2e.",
      },
      {
        t: "p",
        c: "**2c3 2e/4e.** По суті найкращий випадок після 4a1 4e і деяких 0c0 — якщо 2 погані кути на різних шарах DR, це завжди 5–7 ходів до потрійного DR (для 2c3 4e схожа картина). Вивчайте тригери з теорії HTR і якнайбільше сетапів/розширень до них, а також варіації через nissy — наприклад, для «двох квадратів»: `R' F2 R F2 U2 L`, `R' F2 R B2 D2 R`, `R' B2 R D2 B2 R`, `R' B2 R U2 F2 L`. **Світч:** за 1–2 ходи після редукції до 4b2 2e; за 1 хід після зведення до 4b2 4e.",
      },
      {
        t: "p",
        c: "**2c3 6e.** Гірший за попередні — усього пара 7-рухових тригерів, які варто вивчити. Основна стратегія: зведення до 4b2 і світч, хоча можна спробувати й зведення до 4a1 із нісс-трейсингом. **Світч:** за 1–3 ходи до 4b2 2e; за 1–2 ходи до 4b2 4e; за 3–4 ходи до 4a1 4e, якщо нісс-трейс хороший.",
      },
      {
        t: "p",
        c: "**2c3 0e/8e.** Навіть не намагайтесь їх вирішувати.",
      },
      {
        t: "p",
        c: "**4a2.** Варто перевіряти лише з 4 поганими ребрами — навіть якщо ви знайдете HTR для варіанту з 2e, найкоротший HTR цього випадку — 8 ходів, тож рішення все одно не може бути коротшим. Економія часу: `U R2 U2 F2 U` і `U' F2 U2 R2 U'` — варіації слайсу одна одної, перевіряти обидва не потрібно. **Світч:** за 1–4 ходи після редукції до 4a1 4e (за 3–4 — тільки якщо нісс-трейс хороший).",
      },
      {
        t: "p",
        c: "**4b3/4a3.** При редукції з 4b3 до 4a1 стежте, щоб вона проходила через 4a2 4e, а не інший випадок, — інакше ті самі проблеми, що й у не-4e 4a2, плюс зайві ходи. Сабсет іноді дає гарний результат і з 5q через перехід до 4b2 → 2c3, або одразу до 2c4 з 4b3. **Світч:** за 1–3 ходи до 4b2 2e; за 1–2 ходи до 4b2 4e; за 1–2 ходи до 4a2 4e; за 3–4 ходи до 4a1 4e (тут теж дуже корисний нісс-трейс).",
      },
      {
        t: "p",
        c: "**2c4.** Пробуйте зводити переважно до 2c3 2e/4e, хоча 6e іноді теж дає оптимал. Якщо для зведення до 2c3 з обох боків потрібно по 2 подвійні рухи — спробуйте натомість звести до 4b3. Кілька незвичних тригерів: `R U R' B2 R U' R'`, `R U R' F2 L D' L'`, `R' U L' U2 R U' L`, `R' U L' D2 L U' R`. З 2c4 0e можна зробити комутатор кутів до HTR, скасувати його і вставити наприкінці рішення. Якщо зведення до 2c3 вже працює, варіації, що виправляють до 2c5, спрацьовують не завжди — пробуйте більше варіацій. **Світч:** за 1–2 ходи до 2c3 2e/4e; за 3–4 ходи до 4b2 2e (тільки після перевірки всіх редукцій 2c3 2e/4e з niss); до 2c3 6e — якщо у відчаї.",
      },
      {
        t: "p",
        c: "**4b4/4a4.** Переходить у 4a3 з обох боків: з боку 4a сетапте як для 4a2 і робіть qt до 4a3; з боку 4b сетапте як `R U2 R` і робіть `R' U2/D2 R` до 4b2 із парітетом. **Світч:** за 3–4 ходи до 4b2 2e з обох боків скрамблу; за 1–2 ходи після першого qt-прогресу.",
      },
      {
        t: "p",
        c: "**2c5/4b5.** Обидва зводяться до 2c4 і вирішуються звідти. **Світч:** за 1–2 ходи до 2c4; за 3–4 ходи до 2c3 2e/4e.",
      },
      {
        t: "p",
        c: "**0c4.** Найкращий з фальшивих випадків HTR — вирішується `R U2 R` до 4b2 з виправленням парітету, хоча трапляється дуже рідко.",
      },
      {
        t: "p",
        c: "**0c3.** Такий самий поганий, як 5q-випадки — 9-рухове оптимальне HTR попри лише 3 qt, проходить через 4a2. Трапляється ще рідше за 0c4.",
      },
    ],
  },
  {
    id: "after-htr",
    title: "Після HTR",
    kicker: "Метод топів · крок 3",
    blocks: [
      {
        t: "p",
        c: "Отже, ви зробили найскладніше — дійшли до HTR. Але що робити далі? Будь-який HTR можна зібрати за **14 рухів або менше**, тому найпростіша відповідь — знайти пряме рішення методом спроб.",
      },
      {
        t: "p",
        c: "Найпростіший спосіб це зробити — зібрати кути і якнайбільше ребер, а далі використати відомі вставки ребер.",
      },
      {
        t: "p",
        c: "Більш просунутий спосіб — зробити те, що ми робили й раніше, а саме ще одне спрощення куба. Воно називається **Floppy Reduction**, від флопі-куба — кубика 1×3×3, який збирається рухами `R2 L2 F2 B2`. Тобто по осі, яка була віссю нашого DR, треба зробити таку редукцію, а далі дозбирати доміно-шари, не звертаючи уваги на ребра середнього шару (це і є [вставки слайсу](/domino#slice-insertions) — наступний розділ).",
      },
    ],
    resourceGroups: [
      {
        title: "Ресурси",
        items: [
          {
            label: "Advanced Edge Insertion Tutorial [FMC DR/HTR] — вставки ребер після HTR",
            url: "https://youtu.be/Er_qskmjljE?si=CluSdvMv1d9hSvjd",
          },
          {
            label: "Floppy Reduction — документ",
            url: "https://docs.google.com/document/d/1ABI-7W5KpoSavKJuPx_5wlOMWdtgYNpNzCp8fDsEgEw/edit#heading=h.p9pn5vqdadar",
          },
          {
            label: "HTR to FR: Using Floppy Reduction to find optimal half-turn solutions",
            url: "https://youtu.be/ajVWe1h5Ic4?si=76iIBgfiIL72D3VN",
          },
          {
            label: "HTR → double slice (Double Slice Reduction, для допитливих)",
            url: "https://docs.google.com/document/d/1cIftgL8ozyAlBGM3EZcXZy_hE5pNDHYIRDie5R1ocAk/edit?usp=drivesdk",
          },
        ],
      },
    ],
  },
  {
    id: "slice-insertions",
    title: "Вставки слайсу (Leave slice)",
    kicker: "Метод топів · крок 4",
    blocks: [
      {
        t: "p",
        c: "І нарешті, після того як ви зібрали доміно-шари й залишили незібраним середній шар, його треба дозбирати. Для цього всередину рішення після отримання доміно вставляють відповідні рухи середнього шару, що впливають тільки на нього (якщо доміно було по U/D, це будуть рухи `E`).",
      },
      {
        t: "p",
        c: "Більшість людей використовують для цього rNISS, і я залишу для цього ресурси нижче, але особисто мені більше подобається підхід зі вставкою комутаторів та скороченням рухів. Вам потрібно зрозуміти наступні комутатори та їхні подовження, щоб зібрати слайс чи отримати 4x, після чого скоротити рухи, використовуючи теорію слайсу:",
      },
      {
        t: "commutatorTable",
        rows: [
          { alg: "[E, R2]", result: "3e" },
          { alg: "[E, R2/L2 F2]", result: "3e" },
          { alg: "[E2, R2]", result: "2e2e" },
          { alg: "[E2, F2 R2]", result: "2e2e" },
          { alg: "[E2, F2 B2 R2]", result: "2e2e" },
          { alg: "E F2 B2 E F2 B2", result: "4x" },
          { alg: "E R2 F2 R2 E R2 F2 R2", result: "4x" },
          { alg: "[E, R2 F2 R2]", result: "2e2e" },
          { alg: "E R2 E R2", result: "3e4x" },
          { alg: "E R2/L2 F2 E F2 R2/L2", result: "3e4x" },
        ],
      },
    ],
    resourceGroups: [
      {
        title: "Ресурси",
        items: [
          {
            label: "Using rNISS to Find E-Slice Insertions — від автора гіперпарітету",
            url: "https://youtu.be/QWITBAaGnGM",
          },
          {
            label: "Optimizing solutions: n-slice insertions — теорія слайсу від автора «біблії» кх та світового рекордсмена",
            url: "https://sebastiano.tronto.net/speedcubing/slice-theory/",
          },
          {
            label: "How to solve the DR slice for FMC (Examples) — від Тома Нілсона",
            url: "https://youtu.be/coJpRoFaoCs",
          },
          { label: "Slicey Finder — шукач покращень збірки слайсами", url: "https://333.fm/sf" },
          {
            label: "slice better smh — приклад вирішення слайсу від Вена",
            url: "https://docs.google.com/document/d/1V4aa3O40bb9TCMmCSoAcSozO7FdJBihT96jVOBYQiDs/edit",
          },
          {
            label: "VR",
            url: "https://docs.google.com/document/d/1SDdEm4dDDv7jJ3TRjj1QxI-6j7iG0BekJmNRaqaM3kE/edit?usp=drivesdk",
          },
        ],
      },
    ],
  },
  {
    id: "closing",
    title: "І наостанок",
    blocks: [
      {
        t: "note",
        tone: "guide",
        c: "Це точно не все, що можна сказати про вирішення доміно — деякі теми я навіть не чіпав, бо сам не розбираюсь (JZP, JEO тощо, хоча основам з них присвячена [бонусна глава](/bonus)). Але я думаю, що якщо ви подивитесь і попрактикуєте те, що є в цьому гайді, то зможете більш-менш навчитись збирати. Я прекрасно розумію, що все це здається дуже складним, тому з питаннями можете звертатись у [спільноти](/#community), вказані на початку. Удачі в збірках, і сподіваюсь побачитись на змаганнях з кількості ходів!",
      },
    ],
  },
  {
    id: "glossary",
    title: "Термінологія",
    blocks: [
      {
        t: "p",
        c: "Інші терміни, що зустрічаються в обговореннях FMC, але не були розкриті вище:",
      },
      {
        t: "glossary",
        items: GLOSSARY_TERMS,
      },
    ],
  },
  {
    id: "contests",
    title: "Змагання й практика",
    blocks: [
      {
        t: "p",
        c: "Регулярна практика на реальних змаганнях та щотижневих контестах — окремий і дуже дієвий спосіб прогресувати. Ось де можна позмагатись:",
      },
    ],
    contests: true,
  },
];
